import { useState } from 'react';
import { styled, Box, IconButton, Typography, Popover, Divider, useTheme } from '@mui/material';
import { ReactComponent as LogoutIcon } from '../assets/icons/24/sign-out.svg';
import { ReactComponent as EditIcon } from '../assets/icons/24/edit.svg';
import { ReactComponent as ProfileIcon } from '../assets/icons/24/profile.svg';
import { ReactComponent as ProfileIcon48 } from '../assets/icons/48/profile.svg';
import SvgIcon from './SvgIcon';
import LogoutConfirmationDialog from './LogoutConfirmationDialog';
import ChangePasswordDialog from './ChangePasswordDialog';
import { selectUser } from '../store/slices/authSlice';
import { useSelector } from 'react-redux';
import { getThemeColors } from '../helpers/themeStyles';

const ProfileButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 48,
  width: 48,
  boxSizing: 'border-box',
  borderRadius: 18,
  backgroundColor: theme.palette.primary.main,
}));

const ProfileTop = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 24,
});

const MenuSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const MenuRow = styled(Box)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    display: 'flex',
    alignItems: 'center',
    marginLeft: -16,
    marginRight: -16,
    gap: 8,
    height: 52,
    boxSizing: 'border-box',
    padding: '0 16px',
    borderRadius: 8,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.popoverHover,
    },
  };
});

const ProfilePopover = () => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const user = useSelector(selectUser);

  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const isOpen = Boolean(anchorEl);

  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleChangePassword = () => {
    setChangePasswordDialogOpen(true);
  };

  return (
    <>
      <Box sx={{ cursor: 'pointer', width: 48, height: 48 }} onClick={handleButtonClick}>
        {isOpen ? (
          <ProfileButton>
            <SvgIcon icon={ProfileIcon} size={24} color={colors.activeTextOnAccent} />
          </ProfileButton>
        ) : (
          <ProfileIcon48 />
        )}
      </Box>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              width: 300,
              borderRadius: '14px',
              backgroundColor: colors.menuBg,
              overflow: 'visible',
              padding: '32px 24px 16px',
              border: `1px solid ${colors.borderSubtle}`,
              color: colors.textPrimary,
            },
          },
        }}
      >
        <ProfileTop>
          <Typography sx={{ fontSize: 20, fontWeight: 900 }}>
            {user?.name || 'Mon profil'}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, fontWeight: 400, color: colors.textSecondary }}>
            {user?.email || ''}
          </Typography>
        </ProfileTop>

        <MenuSection>
          <MenuRow onClick={handleChangePassword}>
            <SvgIcon icon={EditIcon} color={colors.iconOnSurface} size={20} />
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Changer le mot de passe</Typography>
          </MenuRow>
          <Divider />
          <MenuRow onClick={handleLogout}>
            <SvgIcon icon={LogoutIcon} color={colors.iconOnSurface} size={20} />
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Déconnexion</Typography>
          </MenuRow>
        </MenuSection>
      </Popover>
      <LogoutConfirmationDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      />
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => setChangePasswordDialogOpen(false)}
      />
    </>
  );
};

export default ProfilePopover;
